import type { WorkbookInstance } from "@fortune-sheet/react"

import type { WorkbookRange } from "./rangeDecorations"

/** One frame between checks while a landing settles. */
const SETTLE_INTERVAL_MS = 16
/** Checks the landing has to survive untouched before it counts as settled. */
const SETTLED_CHECKS = 3
/** Hard stop, so a workbook that never settles cannot keep a timer alive. */
const MAX_CHECKS = 60
/** The reviewer's own hand on the grid: from then on the view is theirs. */
const REVIEWER_INPUT = ["wheel", "mousedown", "touchstart", "keydown"] as const

type Offsets = [top: number, left: number]

/**
 * Put a range on screen, on a sheet that has ALREADY been asked to activate,
 * and keep it there until the renderer has finished switching to that sheet.
 *
 * Activation is not one step in the renderer. The render that makes the sheet
 * current is followed by effects that (1) rebuild the row/column offset arrays
 * `scroll` resolves its target through, and (2) reset the scroll position and
 * selection to whatever the renderer last recorded for that sheet, or to the
 * top. A landing made before (1) uses the previous sheet's rows and stops at
 * the wrong place; a landing made before (2) is undone by it. Both happen an
 * unpredictable number of renders after the activation, so no single delay is
 * ever right.
 *
 * So the landing is re-issued every frame, once the sheet is the current one,
 * until it has held untouched for SETTLED_CHECKS frames. Re-issuing is cheap:
 * scrolling to where the view already is fires nothing, and the selection is
 * only set again when something has moved the view or dropped the selection.
 * Any wheel, click or key from the reviewer inside the grid ends the landing,
 * so it never fights them. Returns a function that cancels it.
 */
export function landOnRange({
  workbook,
  isOnSheet,
  sheetId,
  range,
  select,
  scrollbars,
  interruptions,
}: {
  /** The current renderer instance; it is replaced on every render. */
  workbook: () => WorkbookInstance | null
  /** The renderer has made the target sheet the current one. */
  isOnSheet: () => boolean
  sheetId: string
  range: WorkbookRange
  /** Select the range as well (a proof), or only scroll to it (a search hit). */
  select: boolean
  /** The renderer's own scrollbars: the position the reviewer actually sees. */
  scrollbars: () => { x: Element | null; y: Element | null }
  /** Where the reviewer's own input arrives. */
  interruptions?: HTMLElement | null
}): () => void {
  let finished = false
  let timer = 0
  let checks = 0
  let held = 0
  let landedAt: Offsets | null = null
  let selectedAt = Number.NEGATIVE_INFINITY

  const finish = () => {
    if (finished) return
    finished = true
    window.clearTimeout(timer)
    REVIEWER_INPUT.forEach((type) =>
      interruptions?.removeEventListener(type, finish, true),
    )
  }
  REVIEWER_INPUT.forEach((type) =>
    interruptions?.addEventListener(type, finish, {
      capture: true,
      passive: true,
    }),
  )

  const offsets = (): Offsets | null => {
    const { x, y } = scrollbars()
    return x && y ? [y.scrollTop, x.scrollLeft] : null
  }

  const check = () => {
    if (finished) return
    const current = workbook()
    // Unmounted: nothing to land on.
    if (!current) return finish()

    if (isOnSheet()) {
      // Moved since the last landing, by something other than this landing:
      // the renderer's own sheet-switch reset.
      const disturbed = landedAt !== null && !same(offsets(), landedAt)
      // Never twice in a row: the instance answering `getSelection` is the one
      // from before the selection was set until the renderer has re-rendered.
      if (
        select &&
        selectedAt < checks - 1 &&
        (landedAt === null || disturbed || !covers(current, range))
      ) {
        current.setSelection(
          [
            {
              row: [range.startRow, range.endRow],
              column: [range.startColumn, range.endColumn],
            },
          ],
          { id: sheetId },
        )
        selectedAt = checks
      }
      current.scroll({
        targetRow: range.startRow,
        targetColumn: range.startColumn,
      })
      const now = offsets()
      held = !disturbed && same(now, landedAt) ? held + 1 : 0
      landedAt = now
      if (held >= SETTLED_CHECKS) return finish()
    }

    if (checks >= MAX_CHECKS) return finish()
    checks += 1
    timer = window.setTimeout(check, SETTLE_INTERVAL_MS)
  }

  // One tick first, as before: the activation has to be dispatched before
  // anything can land on the sheet it activates.
  timer = window.setTimeout(check, 0)
  return finish
}

const same = (a: Offsets | null, b: Offsets | null) =>
  a !== null && b !== null && a[0] === b[0] && a[1] === b[1]

/**
 * The selection still holds the whole range, both corners. Containment rather
 * than equality, because the renderer widens a selection to the merged cells
 * it touches; the top-left alone is not enough, because the sheet-switch reset
 * can restore an older, narrower selection that starts at the same cell.
 */
const covers = (workbook: WorkbookInstance, range: WorkbookRange) => {
  const selection = workbook.getSelection()?.[0]
  if (!selection) return false
  const [top, bottom] = selection.row
  const [left, right] = selection.column
  return (
    top <= range.startRow &&
    range.endRow <= bottom &&
    left <= range.startColumn &&
    range.endColumn <= right
  )
}
